import {Component, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {first, Observable} from "rxjs";
import {NotificationService} from "../../../../services/notification.service";
import {FeedInRatesService} from "../../../../services/feed-in-rates.service";
import {FeedInRatesDataSource} from "./feed-in-rates.data-source";
import {FeedInRate, FeedInRateUiRow} from "../../../../model/feed-in-rate";
import {MatCalendarCellClassFunction, MatDatepicker} from "@angular/material/datepicker";
import {DateAdapter, MAT_DATE_LOCALE} from "@angular/material/core";
import {MomentUtcDateAdapter} from "../../../../adapter/moment-utc-date-adapter";
import * as moment from 'moment';

@Component({
  selector: 'app-feed-in-rates',
  templateUrl: './feed-in-rates.component.html',
  styleUrls: ['./feed-in-rates.component.scss'],
  providers: [
    {provide: DateAdapter, useClass: MomentUtcDateAdapter, deps: [MAT_DATE_LOCALE]},
  ],
  encapsulation: ViewEncapsulation.None
})
export class FeedInRatesComponent implements OnInit {

  // Columns for the table
  displayedColumns: string[] = ['label', 'value', 'actions'];

  dataSource: FeedInRatesDataSource;
  loading$: Observable<boolean>;

  @ViewChild('picker') picker!: MatDatepicker<any>;

  // Data for the list
  allRates: FeedInRate[] = [];
  selectedRate: FeedInRate | null = null;
  allRatesDates = new Set<string>()
  pickerDate: any = null;

  constructor(
    private feedInRatesService: FeedInRatesService,
    private notification: NotificationService
  ) {
    this.dataSource = new FeedInRatesDataSource(feedInRatesService);
    this.loading$ = this.dataSource.loading$;
  }

  ngOnInit(): void {
    this.feedInRatesService.data$.subscribe(rates => {
      this.allRates = rates || [];
      // Logic to restore the choice after reloading
      this.allRates.forEach(r => {
        if (r.startUpDate) {
          r.startUpDate = moment(r.startUpDate).toISOString();
        }
      });
      this.allRatesDates = new Set<string>();
      this.allRates.forEach((r: FeedInRate) => {
        this.allRatesDates.add(this.toDateString(r.startUpDate));
      });
      if (this.allRates.length > 0) {
        if (this.selectedRate) {
          // Try to select the previously selected element
          const found = this.allRates.find(r => r.id === this.selectedRate?.id);
          this.selectRate(found || this.allRates[0]);
        } else {
          // Standard: newest
          this.selectRate(this.allRates[0]);
        }
      } else {
        // List is empty
        this.selectedRate = null;
        this.dataSource.setData({} as FeedInRate);
      }
    });
  }
  onDateChange(dateFromPicker: any) {
    const searchDate = this.toDateString(dateFromPicker)
    this.cleanupUnsavedDrafts();
    const existingRate = this.allRates.find(rate => this.toDateString(rate.startUpDate)=== searchDate);
    if (existingRate) {
      this.selectRate(existingRate);

    } else {
      this.createNewRate(searchDate);
    }
  }
  private cleanupUnsavedDrafts() {
    if (this.selectedRate) {
      const isUnsaved = !(this.selectedRate as any).id;

      if (isUnsaved) {
        this.allRates = this.allRates.filter(r => r !== this.selectedRate);
        this.allRatesDates.delete(this.toDateString(this.selectedRate.startUpDate));
        this.selectedRate = null;
      }
    }
  }
  /**
   * Gets called if a link in the list is clicked
   */
  selectRate(rate: FeedInRate) {
    this.selectedRate = rate;
    this.pickerDate = rate.startUpDate;
    // Zweiter Parameter false (oder weglassen) -> Unnötige 0-Werte werden ausgeblendet
    this.dataSource.setData(rate, false);
  }

  /**
   * Creates a new entry (locally)
   */
  createNewRate(dateString: string) {
    let newRate: FeedInRate;

    if (this.allRates.length > 0) {
      // create a copy
      newRate = JSON.parse(JSON.stringify(this.allRates[0]));
      delete (newRate as any).id;
      newRate.lastModifiedBy = '';
      (newRate as any).lastModifiedDate = null;
    } else {
      // Fallback: empty objekt
      newRate = {
        countryCode: 'DE',
        startUpDate: moment.utc(dateString, 'YYYY-MM-DD').toISOString(),
      } as FeedInRate;
    }
    newRate.startUpDate = moment.utc(dateString, 'YYYY-MM-DD').toISOString();

    // add to list and select it
    this.allRates.unshift(newRate);
    this.selectRate(newRate);
    this.dataSource.setData(newRate, true);
    this.notification.info("Neuer entwurf angelegt. Bitte Werte prüfen und Speichern.")
  }

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if (view === 'month') {
      if (this.allRatesDates.has(this.toDateString(cellDate))) {
        return 'has-data-dot';
      }
    }
    return '';
  };
  /**
   * Saves the selected entry
   */
  submit() {
    if (!this.selectedRate) return;

    // Get the change from the table
    let payload = this.dataSource.getUpdates();

    // Check if its a new entry
    const isNewEntry = !(this.selectedRate as any).id;

    if (isNewEntry) {
      // New entry
      // If nothing was changed we use the complete selected object
      if (!payload) {
        payload = JSON.parse(JSON.stringify(this.selectedRate));
      }

      // Merge: base-object + changes
      payload = { ...this.selectedRate, ...payload };

      // Be sure the id is not set
      delete (payload as any).id;
    }

    if (!payload && !isNewEntry) {
      this.notification.info("Keine Änderungen zum Speichern");
      return;
    }

    // set date
    payload!.startUpDate = this.selectedRate.startUpDate;
    // set country code
    payload!.countryCode = this.selectedRate.countryCode;

    // API call
    let request$: Observable<FeedInRate>;
    request$ = this.feedInRatesService.saveFeedInRate(payload!);
    request$
      .pipe(first())
      .subscribe({
        next: (savedRate) => {
          this.notification.success("Gespeichert");

          // Update DataSource
          this.dataSource.applyUpdate(savedRate);

          // IMPORTANT: replace the local entry with the one from the server
          const index = this.allRates.indexOf(this.selectedRate!);
          if (index !== -1) {
            this.allRates[index] = savedRate;
          }

          this.selectedRate = savedRate;
          // reload table
          this.selectRate(savedRate);
        },
        error: (err) => {
          console.error(err);
          this.notification.error("Fehler beim Speichern");
        }
      });
  }

  /**
   * Removes a single row from the table (without deleting the whole entry)
   */
  removeRow(row: FeedInRateUiRow) {
    this.dataSource.removeRow(row.key);
  }

  /**
   * Opens the date picker to create or navigate to a new entry
   */
  openNewEntryPicker() {
    this.picker.open();
  }

  /**
   * Delete entry
   */
  deleteSelectedRate() {
    if (!this.selectedRate || !this.selectedRate.id) {
      // If its just a local entry, remove it from the list
      this.allRates.shift();
      if (this.allRates.length > 0) this.selectRate(this.allRates[0]);
      else {
        this.selectedRate = null;
        this.dataSource.setData({} as FeedInRate);
      }
      return;
    }

    if (confirm(`Möchten Sie den Eintrag vom ${new Date(this.selectedRate.startUpDate).toLocaleDateString()} wirklich löschen?`)) {
      this.feedInRatesService.deleteFeedInRate(this.selectedRate.id)
        .pipe(first())
        .subscribe(() => {
        });
    }
  }

  // Helper for HTML (Styling)
  isCurrent(rate: FeedInRate): boolean {
    return this.allRates.length > 0 && rate === this.allRates[0];
  }
  private toDateString(value: any): string {
    if (!value) return '';
    return moment(value).format('YYYY-MM-DD');
  }

}
